import React, { useEffect, useState } from 'react';
import { Table, Button, Badge, Modal, Form } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { alertRemoved } from '../store/alertsSlice';
import api from '../services/api';

const STATUS_CONFIG = {
  active:      { label: 'Active',            bg: 'success',   icon: 'bi-broadcast' },
  resolved:    { label: 'Résolue',           bg: 'info',      icon: 'bi-check-all' },
  rejected:    { label: 'Fausse alerte',     bg: 'danger',    icon: 'bi-x-octagon' },
  cancelled:   { label: 'Désactivée',        bg: 'secondary', icon: 'bi-slash-circle' },
  expired:     { label: 'Expirée',           bg: 'dark',      icon: 'bi-hourglass-bottom' },
};

const PRIORITY_CONFIG = {
  low:      { label: 'Basse',    color: '#888' },
  medium:   { label: 'Moyenne',  color: '#2979ff' },
  high:     { label: 'Haute',    color: '#ff9100' },
  critical: { label: 'Critique', color: '#ff1744' },
};

const KIND_LABELS = {
  accident: '🚗 Accident', traffic: '🚦 Embouteillage', breakdown: '🔧 Panne',
  road_closed: '🚧 Route coupée', obstacle: '⚠️ Obstacle', rain: '🌧️ Pluie intense',
  danger: '💀 Danger', other: '📌 Autre',
};

export default function Alerts() {
  const dispatch = useDispatch();
  const { pending: wsPending } = useSelector((s) => s.alerts);
  const [alerts, setAlerts] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [modalAlert, setModalAlert] = useState(null);
  const [notes, setNotes] = useState('');
  const [priority, setPriority] = useState('medium');

  useEffect(() => {
    loadAlerts();
  }, []);

  const loadAlerts = () => {
    api.get('/alerts/').then((r) => setAlerts(r.data.results || r.data)).catch(() => {});
  };

  const updateAlert = (id, newData) => {
    setAlerts((prev) => prev.map((a) => a.id === id ? { ...a, ...newData } : a));
  };

  // ─── Workflow Actions ───
  const handleResolve = (id) => {
    api.post(`/alerts/${id}/resolve/`, { notes }).then((r) => {
      updateAlert(id, r.data);
      setModalAlert(null);
    }).catch(() => {});
  };

  const handleReject = (id) => {
    api.post(`/alerts/${id}/reject_alert/`, { notes }).then((r) => {
      updateAlert(id, r.data);
      dispatch(alertRemoved(id));
      setModalAlert(null);
    }).catch(() => {});
  };

  const handleCancel = (id) => {
    api.post(`/alerts/${id}/cancel_alert/`, { notes }).then((r) => {
      updateAlert(id, r.data);
      dispatch(alertRemoved(id));
      setModalAlert(null);
    }).catch(() => {});
  };

  const openModal = (alert) => {
    setModalAlert(alert);
    setNotes(alert.notes || '');
    setPriority(alert.priority || 'medium');
  };

  // ─── Combine DB + WS alerts ───
  const allAlerts = [
    ...wsPending.filter((wp) => !alerts.find((a) => a.id === wp.alert_id)).map((wp) => ({
      id: wp.alert_id, kind: wp.kind, status: 'active', lat: wp.lat, lng: wp.lng,
      description: wp.description, reporter_plate: wp.plate, priority: 'medium',
    })),
    ...alerts,
  ].sort((a, b) => b.id - a.id);

  const filtered = filterStatus === 'all' ? allAlerts : allAlerts.filter((a) => a.status === filterStatus);

  // ─── Status counts ───
  const statusCounts = {};
  allAlerts.forEach((a) => { statusCounts[a.status] = (statusCounts[a.status] || 0) + 1; });

  return (
    <div style={{ padding: 20 }}>
      <h4 style={{ color: '#fff', fontWeight: 700, marginBottom: 16 }}>
        <i className="bi bi-exclamation-triangle" style={{ color: '#ff9100' }}></i> Gestion des incidents
      </h4>

      {/* Status filter pills */}
      <div className="d-flex gap-2 flex-wrap mb-3">
        <button
          className={`btn btn-sm ${filterStatus === 'all' ? 'btn-outline-light' : 'btn-outline-secondary'}`}
          onClick={() => setFilterStatus('all')}
        >
          Tous <Badge bg="secondary" className="ms-1">{allAlerts.length}</Badge>
        </button>
        {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
          statusCounts[key] ? (
            <button
              key={key}
              className={`btn btn-sm ${filterStatus === key ? `btn-${cfg.bg}` : `btn-outline-${cfg.bg}`}`}
              onClick={() => setFilterStatus(key)}
            >
              <i className={`bi ${cfg.icon} me-1`}></i>
              {cfg.label} <Badge bg={cfg.bg} className="ms-1">{statusCounts[key]}</Badge>
            </button>
          ) : null
        ))}
      </div>

      <Table variant="dark" hover responsive style={{ fontSize: 13 }}>
        <thead>
          <tr>
            <th>#</th>
            <th>Type</th>
            <th>Priorité</th>
            <th>Statut</th>
            <th>Assigné à</th>
            <th>Véhicule</th>
            <th>Description</th>
            <th>Position</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((a) => {
            const sc = STATUS_CONFIG[a.status] || STATUS_CONFIG.pending;
            const pc = PRIORITY_CONFIG[a.priority] || PRIORITY_CONFIG.medium;
            return (
              <tr key={a.id} style={a.status === 'active' ? { borderLeft: '3px solid #00e676' } : undefined}>
                <td>{a.id}</td>
                <td>{KIND_LABELS[a.kind] || a.kind}</td>
                <td>
                  <span style={{ color: pc.color, fontWeight: 600, fontSize: 12 }}>
                    ● {pc.label}
                  </span>
                </td>
                <td>
                  <Badge bg={sc.bg}>
                    <i className={`bi ${sc.icon} me-1`}></i>{sc.label}
                  </Badge>
                </td>
                <td style={{ fontSize: 12, color: '#aaa' }}>{a.assigned_to_name || a.assigned_to || '-'}</td>
                <td>{a.reporter_plate || '-'}</td>
                <td style={{ maxWidth: 180 }}>{a.description || '-'}</td>
                <td style={{ fontSize: 11 }}>{a.lat?.toFixed(4)}, {a.lng?.toFixed(4)}</td>
                <td>
                  <Button
                    variant="outline-light"
                    size="sm"
                    onClick={() => openModal(a)}
                    title="Gérer l'incident"
                  >
                    <i className="bi bi-pencil-square"></i>
                  </Button>
                </td>
              </tr>
            );
          })}
          {filtered.length === 0 && (
            <tr>
              <td colSpan={9} className="text-center" style={{ color: '#555', padding: 30 }}>Aucun incident</td>
            </tr>
          )}
        </tbody>
      </Table>

      {/* ─── Action Modal ─── */}
      {modalAlert && (
        <Modal show onHide={() => setModalAlert(null)} centered>
          <Modal.Header closeButton style={{ background: '#1a1a2e', border: 'none' }}>
            <Modal.Title style={{ color: '#fff', fontSize: 16 }}>
              <i className="bi bi-shield-exclamation" style={{ color: '#ff9100' }}></i>{' '}
              Incident #{modalAlert.id} — {KIND_LABELS[modalAlert.kind] || modalAlert.kind}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ background: '#1a1a2e' }}>
            <div style={{ color: '#aaa', fontSize: 13, marginBottom: 12 }}>
              {modalAlert.description || 'Pas de description'}
              {modalAlert.reporter_plate && <span className="ms-2" style={{ color: '#888' }}>— {modalAlert.reporter_plate}</span>}
            </div>

            <Form.Group className="mb-3">
              <Form.Label style={{ color: '#ccc', fontSize: 12 }}>Priorité</Form.Label>
              <Form.Select
                size="sm"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                style={{ background: '#0d0d1a', border: '1px solid #333', color: '#fff' }}
              >
                {Object.entries(PRIORITY_CONFIG).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={{ color: '#ccc', fontSize: 12 }}>Notes internes</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                size="sm"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Notes, actions entreprises..."
                style={{ background: '#0d0d1a', border: '1px solid #333', color: '#fff', fontSize: 13 }}
              />
            </Form.Group>

            {/* Workflow buttons depending on current status */}
            <div style={{ borderTop: '1px solid #333', paddingTop: 12 }}>
              <div style={{ color: '#888', fontSize: 11, marginBottom: 8 }}>
                Statut actuel: <Badge bg={STATUS_CONFIG[modalAlert.status]?.bg}>{STATUS_CONFIG[modalAlert.status]?.label}</Badge>
              </div>
              <div className="d-flex gap-2 flex-wrap">
                {modalAlert.status === 'active' && (
                  <>
                    <Button variant="success" size="sm" onClick={() => handleResolve(modalAlert.id)}>
                      <i className="bi bi-check-all"></i> Marquer résolue
                    </Button>
                    <Button variant="outline-danger" size="sm" onClick={() => handleReject(modalAlert.id)}>
                      <i className="bi bi-x-octagon"></i> Fausse alerte
                    </Button>
                    <Button variant="outline-secondary" size="sm" onClick={() => handleCancel(modalAlert.id)}>
                      <i className="bi bi-slash-circle"></i> Désactiver
                    </Button>
                  </>
                )}
              </div>
            </div>
          </Modal.Body>
        </Modal>
      )}
    </div>
  );
}
